import { HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { StateService } from "@uirouter/core";
import { interval, Subscription } from "rxjs";
import { switchMap } from "rxjs/operators";
import { GetStartedModalService } from "../get-started-modal/get-started-modal.service";
import { KeyboardService } from "../keyboard/keyboard.service";
import { GroupService } from "../models/group.service";
import { InstructionsModalService } from "../project-detail/instructions-modal/instructions-modal.service";
import { RememberService } from "../remember/remember.service";
import { SendDataModalService } from "../send-data-modal/send-data-modal.service";
import { ShortcutsModalService } from "../shortcuts-modal/shortcuts-modal.service";

@Component({
  selector: "ab-errors",
  templateUrl: "./errors.component.html",
})
export class ErrorsComponent implements OnInit, OnDestroy {
  @HostBinding("class") public hostClass = "col-xs-12";
  @Input() public project: any;
  @Input() public user: any;
  @Input() public emptyProject: boolean;
  public HTTP_STATUS_TOO_MANY_REQUESTS = 429;
  public EMPTY_PROJECT_POLL_INTERVAL = 5000;
  public groupInterval: Subscription;
  private timeout: number;
  private unregisterKeydown: any;

  constructor(
    public $state: StateService,
    private getStartedModalService: GetStartedModalService,
    private groupService: GroupService,
    private instructionsModalService: InstructionsModalService,
    private keyboardService: KeyboardService,
    private rememberService: RememberService,
    private shortcutsModalService: ShortcutsModalService,
    private sendDataModalService: SendDataModalService,
  ) {}

  public ngOnInit() {
    if (this.emptyProject && !this.sendingDemoData()) {
      this.modalService().open();
      this.startPolling();
    }

    this.unregisterKeydown = this.keyboardService.onKeydown((event: any) => {
      // Don't trigger on CMD+/CTRL+ key combinations e.g. CMD+R to reload.
      if (event.metaKey || event.ctrlKey) {
        return;
      }

      // Shift+? (191=?)
      if (!(event.keyCode === 191 && event.shiftKey)) {
        return;
      }

      this.shortcutsModalService.toggle();
      event.preventDefault();
    });
  }

  public ngOnDestroy() {
    if (this.groupInterval) {
      this.cancelInterval();
    }
    this.unregisterKeydown();
    if (this.timeout) {
      window.clearTimeout(this.timeout);
    }
  }

  private modalService() {
    if (
      this.project.isFirstProject &&
      !this.project.demoModeUntilDate &&
      !this.rememberService.get("sendDataModalDismissed")
    ) {
      return this.sendDataModalService;
    }

    if (this.rememberService.get("getStartedModalDismissed")) {
      return this.instructionsModalService;
    }

    return this.getStartedModalService;
  }

  private startPolling() {
    this.groupInterval = interval(this.EMPTY_PROJECT_POLL_INTERVAL)
      .pipe(
        switchMap(() => {
          return this.groupService.query({
            projectId: this.project.id,
            limit: 1,
          });
        }),
      )
      .subscribe(
        (resp: any) => {
          if (resp.groups.length > 0) {
            this.cancelInterval();
            this.reloadPage();
          }
        },
        (error: HttpErrorResponse) => {
          if (error.status === this.HTTP_STATUS_TOO_MANY_REQUESTS) {
            this.cancelInterval();

            const waitTimeSec = Number(error.headers?.get("x-ratelimit-delay"));
            if (!waitTimeSec) {
              return;
            }
            this.timeout = window.setTimeout(() => {
              this.startPolling();
            }, waitTimeSec * 1000);
          }
        },
      );
  }

  private reloadPage() {
    window.location.reload();
  }

  private cancelInterval() {
    this.groupInterval.unsubscribe();
  }

  private sendingDemoData(): boolean {
    if (!this.project.demoModeUntilDate) {
      return false;
    }

    return new Date() < new Date(this.project.demoModeUntilDate);
  }
}
